import { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Form, Badge ,Button} from "react-bootstrap";
import { StarFill, Search, FilterCircle, BoxSeam, X  } from "react-bootstrap-icons";
import "animate.css";
import "../styles/products.css";

// Import Supabase client
import { createClient } from "@supabase/supabase-js";

// Supabase configuration - replace with your actual config
const supabaseUrl = "https://aeauzrauwtlwvhzafpwm.supabase.co";
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlYXV6cmF1d3Rsd3ZoemFmcHdtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzkyMjM0OTgsImV4cCI6MjA1NDc5OTQ5OH0.mWw7stDMNG-ceiUqA50bcrkcNxf0HhUtSfzh3nd-t7Y";

// Initialize Supabase client
const supabase = createClient(supabaseUrl, supabaseKey);

const ProductCard = ({ product }) => {
  return (
    <div className="product-card animate__animated animate__fadeInUp">
      <div className="product-badge">
        <Badge bg="primary" className="product-category">{product.category}</Badge>
      </div>

      <div className="product-image-container">
        <img src={product.image} alt={product.name} className="product-image" />
        <div className="product-brand-tag">{product.brand}</div>
      </div>
      
      <div className="product-content">
        <h3 className="product-title">{product.name}</h3>
        
        <div className="product-specs-container">
          {product.specs.map((spec, index) => (
            <div key={index} className="product-spec-item">
              <span className="spec-label">{spec.label}</span>
              <span className="spec-value">{spec.value}</span>
            </div>
          ))}
        </div>
        
        <div className="product-footer">
          <div className="product-price">
            {product.price.toLocaleString("fr-FR")} <span className="currency">DA</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [priceSort, setPriceSort] = useState("none");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

    // Function to reset all filters
  const resetFilters = () => {
    setSelectedCategory("all");
    setSelectedBrand("all");
    setPriceSort("none");
    setSearchQuery("");
  };

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return (
      selectedCategory !== "all" ||
      selectedBrand !== "all" ||
      priceSort !== "none" ||
      searchQuery !== ""
    );
  }, [selectedCategory, selectedBrand, priceSort, searchQuery]);


  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Fetch products from Supabase
        const { data, error } = await supabase
          .from("products")
          .select("*");

        if (error) {
          throw error;
        }
        
        setProducts(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Impossible de charger les produits. Veuillez réessayer plus tard.");
        
        // Fallback to local JSON if Supabase fails
        try {
          const response = await fetch("/src/data/products.json");
          const data = await response.json();
          setProducts(data);
          setError(null);
        } catch (jsonError) {
          console.error("Error fetching fallback products:", jsonError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Get unique categories and brands for filter dropdowns
  const categories = useMemo(() => {
    const categorySet = new Set(products.map(product => product.category));
    return Array.from(categorySet);
  }, [products]);

  const brands = useMemo(() => {
    const brandSet = new Set(products.map(product => product.brand));
    return Array.from(brandSet);
  }, [products]);

  const filteredProducts = useMemo(() => {
    let filtered = products.filter((product) => {
      const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
      const matchesBrand = selectedBrand === "all" || product.brand === selectedBrand;
      const matchesSearch = searchQuery === "" || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        product.brand.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesBrand && matchesSearch;
    });

    // Apply price sorting
    if (priceSort === "asc") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (priceSort === "desc") {
      filtered.sort((a, b) => b.price - a.price);
    }
    filtered.sort((a, b) => b.category.localeCompare(a.category));

    return filtered;
  }, [selectedCategory, selectedBrand, priceSort, searchQuery, products]);

return (
    <div className="products-section">
      <Container fluid className="py-5">
        <div className="products-header">
          <h1 className="products-title">
            <BoxSeam className="title-icon" /> Nos Produits
          </h1>

          <div className="search-container">
            <div className="search-input-wrapper">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search-btn"
                  onClick={() => setSearchQuery("")}
                  aria-label="Effacer la recherche"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            
            <div className="filter-controls">
{hasActiveFilters && (
  <button
    onClick={resetFilters}
    className="creative-reset-btn"
    aria-label="Réinitialiser les filtres"
  >
    <span className="reset-icon-wrapper">
      <X size={16} className="reset-icon" />
    </span>
    <span className="reset-text"></span>
    <span className="reset-underline"></span>
  </button>
)}
              <button 
                className="filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
                aria-expanded={showFilters}
              >
                <FilterCircle className="filter-icon" />
                <span>Filtres</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className={`filters-container ${showFilters ? 'show' : ''}`}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="filter-label">Catégorie</Form.Label>
                <Form.Select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">Toutes les catégories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="filter-label">Marque</Form.Label>
                <Form.Select 
                  value={selectedBrand} 
                  onChange={(e) => setSelectedBrand(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">Toutes les marques</option>
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="filter-label">Trier par prix</Form.Label>
                <Form.Select 
                  value={priceSort} 
                  onChange={(e) => setPriceSort(e.target.value)} 
                  className="filter-select"
                >
                  <option value="none">Par défaut</option>
                  <option value="asc">Prix croissant</option>
                  <option value="desc">Prix décroissant</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>
        
        {loading && (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Chargement des produits...</p>
          </div>
        )}
        
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
        
        {!loading && !error && (
          <>
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            {filteredProducts.length === 0 && (
              <div className="no-results">
                <h3>Aucun produit trouvé</h3>
                <p>Essayez de modifier vos filtres de recherche</p>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default Products;